import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { getName, phoneNumberFormatting } from "js/formatting";
import { useTranslation } from "react-i18next";

import Badge from "components/General/Badge";
import UserAvatarDialog from "./Dialogs/UserAvatarDialog";
import { getImage } from "js/utilities";

const AgentProfileCard = ({ data, agentProfile }) => {
  const { user } = useSelector((s) => s.user);
  const [openUserAvatarDialog, setOpenUserAvatarDialog] = useState(false);
  const [managerName, setManagerName] = useState();
  const { t } = useTranslation();

  const mobileNumber = user?.mobile;
  const emailAddress = agentProfile?.email?.address;

  const businessAddress = data?.addresses.find(
    (address) => address.type === "BUSN"
  );
  const businessPhone = data?.phones?.find((phone) => phone?.type === "BUSN");
  const faxNumber = data?.phones?.find((phone) => phone?.type === "FAX");
  const homePhone = data?.phones?.find((phone) => phone?.type === "HOME");

  // find logged in user with user object id
  useEffect(() => {
    if (user?.role?.roleName === "Agent Sub-Producer") {
      let managerInfo = user?.agentHierarchy?.find((x) => x.isMasterAgent);
      if (managerInfo) {
        let name = getName(managerInfo?.agentInfo?.parties?.person);
        setManagerName(name);
      }
    }
  }, [user]);

  const getBadgeInfo = () => {
    switch (data?.status) {
      case "H":
        return { status: "good", text: "Home Office" };

      case "I":
        return { status: "problem", text: "Inactive" };

      case "T":
        return {
          status: "risk",
          text: "Terminated & Non-vested",
        };

      case "V":
        return { status: "risk", text: "Terminated & Vested" };

      default:
        return { status: "good", text: "Active" };
    }
  };

  return (
    <>
      <div className="container ui-component-spacing">
        <div className="ui-card p-4 xl:p-6">
          <div className="flex flex-col md:flex-row">
            <div className="mb-4 mr-auto sm:w-60 lg:w-1/5 md:mr-6">
              <div className="relative">
                <img
                  src={getImage("image11.png")}
                  alt="avatar"
                  className="w-full h-auto max-w-none"
                />
                {/* <button
                  className="absolute group shadow-md bottom-2 right-2 rounded-full border border-transparent text-theme-action hover:text-theme-action hover:border-white"
                  onClick={() => setOpenUserAvatarDialog(true)}
                >
                  <svg
                    className="ui-icon h-6 w-6"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                    aria-label="Edit your account photo"
                  >
                    <g className="group-hover:hidden">
                      <use href="#ui-icon-edit-stroke"></use>
                    </g>
                    <g className="hidden group-hover:block">
                      <use href="#ui-icon-edit-solid"></use>
                    </g>
                  </svg>
                </button> */}
              </div>
            </div>
            <div className="w-full lg:w-4/5">
              <div className="text-lg font-semibold flow-root">
                {getName(agentProfile?.person)}
                <span className="ml-2 text-base font-normal">
                  {data?.jobInfo?.title}
                </span>
                <div className="mt-2 sm:mt-0 sm:float-right">
                  <Badge
                    status={getBadgeInfo().status}
                    statusText={getBadgeInfo().text}
                    srPrefix="Agent Status"
                  />
                </div>
              </div>

              <hr />
              <div className="grid grid-cols-1 gap-4 items-start sm:grid-cols-2 lg:grid-cols-4 xl:gap-6">
                <div>
                  <div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentProfileCard-agentNumber")}
                    </div>
                    <div className="text-sm text-theme-text-secondary font-semibold">
                      {user?.agentAlternateViewingId}
                    </div>
                  </div>
                  <br />
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("AgentProfileCard-businessAddress")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    <div>{businessAddress?.addressLine1}</div>
                    <div>{businessAddress?.addressLine2}</div>
                    <div>
                      {businessAddress?.city},{" "}
                      {businessAddress?.state?.toUpperCase()}{" "}
                      {businessAddress?.zip?.substring(0, 5)}
                    </div>
                  </div>
                  <br />
                  <div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentProfileCard-businessNumber")}
                    </div>
                    <div className="text-sm text-theme-text-secondary font-semibold">
                      {businessPhone?.phoneNumber
                        ? phoneNumberFormatting(businessPhone?.phoneNumber)
                        : "N/A"}
                    </div>
                  </div>
                  <br />
                  <div>
                    <div>
                      <div className="text-xs text-theme-text-tertiary">
                        {t("AgentProfileCard-manager")}
                      </div>
                      <div className="text-sm text-theme-text-secondary font-semibold">
                        {managerName ?? "N/A"}
                      </div>
                    </div>
                  </div>
                  <br />
                  <div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentProfileCard-faxNumber")}
                    </div>
                    <div className="text-sm text-theme-text-secondary font-semibold">
                      {faxNumber?.phoneNumber
                        ? phoneNumberFormatting(faxNumber?.phoneNumber)
                        : "N/A"}
                    </div>
                  </div>
                </div>
                <div>
                  <div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentProfileCard-homeNumber")}
                    </div>
                    <div className="text-sm text-theme-text-secondary font-semibold">
                      {homePhone?.phoneNumber
                        ? phoneNumberFormatting(homePhone?.phoneNumber)
                        : "N/A"}
                    </div>
                  </div>
                  <br />
                  {mobileNumber && (
                    <>
                      <div>
                        <div className="text-xs text-theme-text-tertiary">
                          {t("AgentProfileCard-mobileNumber")}
                        </div>
                        <div className="text-sm text-theme-text-secondary font-semibold">
                          {mobileNumber}
                        </div>
                      </div>
                      <br />
                    </>
                  )}
                  <div>
                    <div className="text-xs text-theme-text-tertiary">
                      {t("AgentProfileCard-email")}
                    </div>
                    <div className="text-sm font-semibold text-theme-text-secondary">
                      <div>{emailAddress === "" ? "N/A" : emailAddress}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UserAvatarDialog
        state={openUserAvatarDialog}
        setState={setOpenUserAvatarDialog}
      />
    </>
  );
};

export default AgentProfileCard;
